import { useState, useEffect } from 'react';
import { Plus, ArrowRight, MessageSquare, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import type { FilterCategory } from '../../types/analytics.types';
import { categoryColor, type FilterFieldConfig } from '../../lib/filterConfig';
import { useCatalogStore } from '../../stores/catalogStore';
import { FilterRow } from './FilterRow';
import { OutputSelector } from './OutputSelector';
import { useAnalyticsStore } from '../../stores/analyticsStore';
import { usePreviewCount, useExecuteQuery } from '../../hooks/useAnalyticsQuery';
import { formatNumber } from '../../lib/formatters';

interface QueryBuilderProps {
  onSwitchMode: () => void;
}

export function QueryBuilder({ onSwitchMode }: QueryBuilderProps) {
  const [addFilterOpen, setAddFilterOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<FilterCategory | null>(null);

  const {
    filters,
    outputConfig,
    previewCount,
    previewLoading,
    addFilter,
    updateFilter,
    removeFilter,
    setOutputConfig,
    buildQueryFromState,
    setUIState,
    setCurrentQuery,
  } = useAnalyticsStore();

  const { refetch: refetchPreview } = usePreviewCount(filters, filters.length > 0);
  const executeQuery = useExecuteQuery();

  useEffect(() => {
    if (filters.length > 0) {
      const debounceTimer = setTimeout(() => {
        refetchPreview();
      }, 500);
      return () => clearTimeout(debounceTimer);
    }
  }, [filters, refetchPreview]);

  const catalogCategories = useCatalogStore((s) => s.categories);
  const catalogFields = useCatalogStore((s) => s.fields);
  const fieldsFor = (cat: FilterCategory) => catalogFields.filter((f) => f.category === cat);
  const labelFor = (cat: FilterCategory) => catalogCategories.find((c) => c.key === cat)?.label ?? cat;
  const categories = catalogCategories.map((c) => c.key).filter((cat) => fieldsFor(cat).length > 0);

  const handleAddFilter = (fieldConfig: FilterFieldConfig) => {
    const defaultOperator = fieldConfig.operators[0];
    let defaultValue = null;

    if (fieldConfig.valueType === 'boolean') {
      defaultValue = null;
    } else if (fieldConfig.valueType === 'number' && defaultOperator === 'between') {
      defaultValue = { from: 0, to: 100 };
    }

    addFilter({
      category: fieldConfig.category,
      field: fieldConfig.field,
      operator: defaultOperator,
      value: defaultValue,
    });

    setAddFilterOpen(false);
    setSelectedCategory(null);
  };

  const handleRunQuery = () => {
    if (filters.length === 0) return;

    const query = buildQueryFromState();
    setCurrentQuery(query);
    setUIState('confirming');
  };

  const handleExecuteDirectly = () => {
    if (filters.length === 0) return;

    const query = buildQueryFromState();
    executeQuery.mutate({ query });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-lg font-semibold">Build Your Search</h3>
        <Button variant="ghost" size="sm" onClick={onSwitchMode}>
          <MessageSquare className="h-4 w-4 mr-2" />
          Use natural language instead
        </Button>
      </div>

      <div className="space-y-2">
        {filters.map((filter) => (
          <FilterRow
            key={filter.id}
            filter={filter}
            onUpdate={(updates) => updateFilter(filter.id, updates)}
            onRemove={() => removeFilter(filter.id)}
          />
        ))}

        {filters.length === 0 && (
          <div className="text-center py-8 text-muted-foreground border rounded-lg border-dashed">
            Add filters to find specific patients. Click "Add Filter" to get started.
          </div>
        )}
      </div>

      <Popover open={addFilterOpen} onOpenChange={setAddFilterOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add Filter
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" align="start">
          {selectedCategory === null ? (
            <div className="space-y-1">
              <div className="text-sm font-medium mb-2">Select Category</div>
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm hover:bg-accent ${categoryColor(category)}`}
                >
                  {labelFor(category)}
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-1">
              <button
                onClick={() => setSelectedCategory(null)}
                className="text-sm text-muted-foreground hover:text-foreground mb-2"
              >
                ← Back to categories
              </button>
              <div className="text-sm font-medium mb-2">
                {labelFor(selectedCategory)}
              </div>
              {fieldsFor(selectedCategory).map((field) => (
                <button
                  key={field.field}
                  onClick={() => handleAddFilter(field)}
                  className="w-full text-left px-3 py-2 rounded-md text-sm hover:bg-accent"
                >
                  {field.label}
                </button>
              ))}
            </div>
          )}
        </PopoverContent>
      </Popover>

      <OutputSelector config={outputConfig} onChange={setOutputConfig} />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-4 border-t">
        <div className="text-sm">
          {previewLoading ? (
            <Skeleton className="h-5 w-32" />
          ) : filters.length > 0 && previewCount !== null ? (
            <span className="text-muted-foreground">
              <span className="font-semibold text-foreground">
                {formatNumber(previewCount)}
              </span>{' '}
              patients match
            </span>
          ) : (
            <span className="text-muted-foreground">Add filters to see count</span>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={handleRunQuery}
            disabled={filters.length === 0}
          >
            <Eye className="h-4 w-4 mr-2" />
            Review
          </Button>
          <Button
            onClick={handleExecuteDirectly}
            disabled={filters.length === 0 || executeQuery.isPending}
          >
            {executeQuery.isPending ? (
              'Finding patients...'
            ) : (
              <>
                Show Results
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
